export type TripItemSource = "PORTABILITY_PURCHASE" | "EXTERNAL_MANUAL" | "MANUAL_PLAN" | "CUSTOM_NOTE";
export type PlannerOutcome = "FEASIBLE" | "RISKY" | "NOT_FEASIBLE" | "INSUFFICIENT_DATA";
export type OfflineSyncState = "not_saved" | "synced" | "pending" | "conflict" | "saving" | "syncing" | "unavailable";

export interface TripSummary {
  readonly tripId: string;
  readonly name: string;
  readonly destination: string;
  readonly startsOn: string;
  readonly endsOn: string;
  readonly timeZone?: string;
}

export type TripTemporalStatus = "current" | "upcoming" | "past";

export interface TripItineraryItem {
  readonly itemId: string;
  readonly tripId: string;
  readonly source: TripItemSource;
  readonly title: string;
  readonly localDate?: string;
  readonly startsAt?: string;
  readonly endsAt?: string;
  readonly location?: string;
  readonly shareLocation?: boolean;
  readonly privateNote?: string;
  readonly completedAt?: string;
  readonly manualSortOrder?: number;
}

export interface TemporalItineraryGroup {
  readonly key: string;
  readonly label: string;
  readonly items: readonly (TripItineraryItem & { readonly deEmphasized: boolean })[];
}

export interface TripTemporalGroup {
  readonly status: TripTemporalStatus;
  readonly label: string;
  readonly trips: readonly TripSummary[];
}

export interface OfflineTripStatus {
  readonly tripId: string;
  readonly state: OfflineSyncState;
  readonly savedAt?: string;
  readonly queuedMutations?: number;
  readonly error?: string;
}

export interface TripShareState {
  readonly tripId: string;
  readonly publicPath?: string;
  readonly expiresAt?: string;
  readonly revoked?: boolean;
}

export interface PlannerAcknowledgementSummary {
  readonly required: true;
  readonly acknowledged: boolean;
  readonly statement: string;
  readonly advisoryCopyVersion?: string;
  readonly plannerVersion?: string;
}

export interface PlannerResultSummary {
  readonly outcome: PlannerOutcome;
  readonly explanations: readonly string[];
  readonly missingInputs: readonly string[];
  readonly advisoryAcknowledgement: PlannerAcknowledgementSummary;
}

export interface AgenticToolResult {
  readonly tool: string;
  readonly status: "used" | "skipped" | "blocked";
  readonly summary: string;
}

export interface AgenticTextResult {
  readonly requestId: string;
  readonly text: string;
  readonly serverMediated: true;
  readonly modelOutputTrustedAsAuthority: false;
  readonly consequentialMutationOccurred: false;
  readonly toolResults: readonly AgenticToolResult[];
  readonly groundedAt?: string;
}

export interface PlannerRelianceState {
  readonly canShowResult: boolean;
  readonly heading: string;
  readonly copy: string;
}

export interface OfflineStatusPresentation {
  readonly label: string;
  readonly detail: string;
  readonly needsAttention: boolean;
}

export interface AgenticGroundingPresentation {
  readonly heading: string;
  readonly safetyCopy: string;
  readonly toolSummaries: readonly string[];
}

export interface PersistedTripWorkspaceState {
  readonly selectedTripId: string | null;
  readonly offlineTripIds: readonly string[];
}

export interface ReopenedTripWorkspaceState {
  readonly selectedTripId: string | null;
  readonly savedOfflineTripIds: readonly string[];
}

function parseMaybeInstant(value: string | undefined): number | null {
  if (value === undefined) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function dayKey(item: TripItineraryItem): string {
  if (item.localDate !== undefined && /^\d{4}-\d{2}-\d{2}$/.test(item.localDate)) return item.localDate;
  if (item.startsAt !== undefined && /^\d{4}-\d{2}-\d{2}/.test(item.startsAt)) return item.startsAt.slice(0, 10);
  return "unscheduled";
}

function formatDayLabel(key: string): string {
  if (key === "unscheduled") return "Unscheduled";
  const date = new Date(`${key}T12:00:00.000Z`);
  if (!Number.isFinite(date.getTime())) return key;
  return new Intl.DateTimeFormat("en-US", { weekday: "short", month: "short", day: "numeric" }).format(date);
}

function todayKey(now: string): string {
  const parsed = parseMaybeInstant(now);
  if (parsed === null) return new Date().toISOString().slice(0, 10);
  return new Date(parsed).toISOString().slice(0, 10);
}

function tripTemporalStatus(trip: TripSummary, today: string): TripTemporalStatus {
  if (trip.endsOn < today) return "past";
  if (trip.startsOn > today) return "upcoming";
  return "current";
}

function itemTime(item: TripItineraryItem): number {
  return parseMaybeInstant(item.startsAt) ?? Number.POSITIVE_INFINITY;
}

function itemSourceRank(item: TripItineraryItem): number {
  if (item.source === "PORTABILITY_PURCHASE") return 0;
  if (item.source === "EXTERNAL_MANUAL") return 1;
  if (item.source === "MANUAL_PLAN") return 2;
  return 3;
}

function isPastItem(item: TripItineraryItem, nowMs: number): boolean {
  if (item.completedAt !== undefined) return true;
  const endsAt = parseMaybeInstant(item.endsAt);
  if (endsAt !== null) return endsAt < nowMs;
  const startsAt = parseMaybeInstant(item.startsAt);
  return startsAt !== null && startsAt < nowMs;
}

export function groupTripItemsByDay(items: readonly TripItineraryItem[], now: string): readonly TemporalItineraryGroup[] {
  const nowMs = parseMaybeInstant(now) ?? Date.now();
  const buckets = new Map<string, (TripItineraryItem & { readonly deEmphasized: boolean })[]>();
  for (const item of items) {
    const key = dayKey(item);
    const bucket = buckets.get(key) ?? [];
    bucket.push(Object.freeze({ ...item, deEmphasized: isPastItem(item, nowMs) }));
    buckets.set(key, bucket);
  }
  return [...buckets.entries()]
    .sort(([left], [right]) => {
      if (left === right) return 0;
      if (left === "unscheduled") return 1;
      if (right === "unscheduled") return -1;
      return left.localeCompare(right);
    })
    .map(([key, bucket]) => Object.freeze({
      key,
      label: formatDayLabel(key),
      items: Object.freeze(bucket.sort((left, right) =>
        itemSourceRank(left) - itemSourceRank(right)
        || (left.manualSortOrder ?? Number.MAX_SAFE_INTEGER) - (right.manualSortOrder ?? Number.MAX_SAFE_INTEGER)
        || itemTime(left) - itemTime(right)
        || left.title.localeCompare(right.title)
        || left.itemId.localeCompare(right.itemId)
      ))
    }));
}

export function groupTripsByTemporalStatus(trips: readonly TripSummary[], now: string): readonly TripTemporalGroup[] {
  const today = todayKey(now);
  const groups: Record<TripTemporalStatus, TripSummary[]> = {
    current: [],
    upcoming: [],
    past: []
  };
  for (const trip of trips) groups[tripTemporalStatus(trip, today)].push(trip);
  groups.current.sort((left, right) => left.endsOn.localeCompare(right.endsOn) || left.startsOn.localeCompare(right.startsOn) || left.name.localeCompare(right.name));
  groups.upcoming.sort((left, right) => left.startsOn.localeCompare(right.startsOn) || left.name.localeCompare(right.name));
  groups.past.sort((left, right) => right.endsOn.localeCompare(left.endsOn) || left.name.localeCompare(right.name));
  return Object.freeze([
    Object.freeze({ status: "current" as const, label: "Current", trips: Object.freeze(groups.current) }),
    Object.freeze({ status: "upcoming" as const, label: "Upcoming", trips: Object.freeze(groups.upcoming) }),
    Object.freeze({ status: "past" as const, label: "Past", trips: Object.freeze(groups.past) })
  ]);
}

export function resolveReopenedTripWorkspaceState(
  trips: readonly TripSummary[],
  persisted: PersistedTripWorkspaceState | null,
  now: string
): ReopenedTripWorkspaceState {
  const groupedTrips = groupTripsByTemporalStatus(trips, now).flatMap((group) => group.trips);
  const selectedTripId = persisted?.selectedTripId !== null && persisted?.selectedTripId !== undefined && trips.some((trip) => trip.tripId === persisted.selectedTripId)
    ? persisted.selectedTripId
    : groupedTrips[0]?.tripId ?? null;
  const knownTripIds = new Set(trips.map((trip) => trip.tripId));
  const savedOfflineTripIds = (persisted?.offlineTripIds ?? []).filter((tripId) => knownTripIds.has(tripId));
  return Object.freeze({ selectedTripId, savedOfflineTripIds: Object.freeze(savedOfflineTripIds) });
}

export function presentOfflineStatus(status: OfflineTripStatus | undefined): OfflineStatusPresentation {
  if (status === undefined || status.state === "not_saved") {
    return { label: "Not saved offline", detail: "Save key trip details before travel.", needsAttention: false };
  }
  if (status.state === "synced") {
    return { label: "Saved offline", detail: status.savedAt === undefined ? "Key trip details are saved on this device." : `Saved ${status.savedAt}.`, needsAttention: false };
  }
  if (status.state === "pending") {
    const queued = status.queuedMutations ?? 0;
    return { label: "Sync pending", detail: queued > 0 ? `${queued} queued change${queued === 1 ? "" : "s"} will sync when online.` : "A queued offline change will sync when online.", needsAttention: true };
  }
  if (status.state === "conflict") {
    return { label: "Needs review", detail: status.error ?? "The server reported a conflict. Nothing was dropped.", needsAttention: true };
  }
  if (status.state === "saving") return { label: "Saving offline", detail: "Saving key trip details.", needsAttention: false };
  if (status.state === "syncing") return { label: "Syncing", detail: "Updating your saved trip changes.", needsAttention: false };
  return { label: "Offline unavailable", detail: status.error ?? "Offline storage is not available on this device.", needsAttention: true };
}

export function plannerRelianceState(result: PlannerResultSummary | null): PlannerRelianceState {
  if (result === null) {
    return {
      canShowResult: false,
      heading: "No timing check yet",
      copy: "Run a timing check after confirming the estimate is advisory."
    };
  }
  if (!result.advisoryAcknowledgement.acknowledged) {
    return {
      canShowResult: false,
      heading: "Acknowledgement required",
      copy: result.advisoryAcknowledgement.statement
    };
  }
  return {
    canShowResult: true,
    heading: result.outcome.replaceAll("_", " "),
    copy: "This timing check is advisory. It does not guarantee timing, inventory, bookings, or availability."
  };
}

export function presentAgenticGrounding(result: AgenticTextResult | null): AgenticGroundingPresentation {
  if (result === null) {
    return {
      heading: "No travel-help answer yet",
      safetyCopy: "Travel help appears after Portability responds.",
      toolSummaries: []
    };
  }
  return {
    heading: "Portability answer",
    safetyCopy: "Use this as planning context only. It is not a booking, payment, or timing guarantee.",
    toolSummaries: result.toolResults.map((tool) => `${tool.tool}: ${tool.status} - ${tool.summary}`)
  };
}
